import * as React from 'react'
import { Radio, RadioGroup } from '@headlessui/react'
import classnames from 'classnames'
import { useThemeContext } from '@/contexts/ThemeContext'
import { CaptionSM } from '../Text'

interface PillGroupOption {
  /**
   * Label to be displayed for the option
   *
   * @required
   * @type string
   */
  label: string
  value: string
}

interface PillGroupProps {
  /**
   * Additional class names to apply to the PillGroup container
   *
   * @optional
   * @type string
   */
  className?: string
  /**
   * Default selected value for the PillGroup
   *
   * @optional
   * @type string
   */
  defaultValue?: string
  /**
   * Radio options to be rendered within the PillGroup
   *
   * @required
   * @type PillGroupOption[]
   */
  options: PillGroupOption[]
  /**
   * Currently selected value for the PillGroup
   *
   * @optional
   * @type string
   */
  value?: string
  /**
   * Callback fired when the selected value changes
   * @param {string} value The new selected value
   * @returns {void}
   */
  onChange?: (value: string) => void
  ref?: React.Ref<HTMLDivElement>
}

/**
 * PillGroup component allows users to select one option from a set of pill-shaped buttons.
 * @param {PillGroupProps} props - The props for the component
 */
const PillGroup = ({ className, defaultValue, options, value, onChange, ...rest }: PillGroupProps) => {
  const { isDarkMode } = useThemeContext()

  const baseClasses = 'inline-flex cursor-pointer rounded-[50px] border border-core-gray-200 px-4 py-[6px]'
  const themeSelectedClasses = isDarkMode ? 'bg-white border-white text-black' : 'bg-black border-black text-white'

  return (
    <RadioGroup
      onChange={onChange}
      className={classnames('flex flex-row', className)}
      defaultValue={defaultValue}
      value={value}
      {...rest}
    >
      {options.map((option) => (
        <Radio key={option.value} value={option.value}>
          {({ checked }) => (
            <span className={classnames(baseClasses, checked ? themeSelectedClasses : '')}>
              <CaptionSM>{option.label}</CaptionSM>
            </span>
          )}
        </Radio>
      ))}
    </RadioGroup>
  )
}

export { PillGroup, type PillGroupOption }
